import { BsCheckLg, BsXCircle } from 'react-icons/bs';
import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import { formatAuditDateTime } from '../../../lib/auditColumnDefs';
import { collectUniqueScalarValues } from '../../../components/shared/GridColumnFilterPanel';
import {
  CopyableGridText,
  EmptyGridCell,
  GridCellWrap,
  StatusBadgeCellRenderer,
  SubtitleCellRenderer,
} from '../../../components/shared/agGridCells';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import type {
  StorefrontUrlReviewColumnFilterKey,
  StorefrontUrlReviewContext,
  StorefrontUrlSubmissionRow,
} from '../models/types';

type StorefrontUrlReviewFilterColumnConfig = {
  key: StorefrontUrlReviewColumnFilterKey;
  label: string;
  type?: 'select' | 'dateRange';
  getOptions?: (rows: StorefrontUrlSubmissionRow[]) => string[];
};

export const STOREFRONT_URL_REVIEW_FILTER_COLUMNS: StorefrontUrlReviewFilterColumnConfig[] = [
  {
    key: 'merchantName',
    label: 'Merchant',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.merchantName || '—')),
  },
  {
    key: 'url',
    label: 'URL',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.url || '—')),
  },
  {
    key: 'status',
    label: 'Status',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.status || 'UNKNOWN')),
  },
  {
    key: 'rejectionReason',
    label: 'Reason',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.rejectionReason || '—')),
  },
  {
    key: 'createdAt',
    label: 'Created At',
    type: 'dateRange',
  },
];

export const STOREFRONT_URL_REVIEW_COLUMN_LABELS = Object.fromEntries(
  STOREFRONT_URL_REVIEW_FILTER_COLUMNS.map(({ key, label }) => [key, label]),
) as Record<StorefrontUrlReviewColumnFilterKey, string>;

function columnLabel(key: StorefrontUrlReviewColumnFilterKey) {
  return STOREFRONT_URL_REVIEW_COLUMN_LABELS[key];
}

export function buildStorefrontUrlReviewColumnFilterFields(sourceRows: StorefrontUrlSubmissionRow[]) {
  return STOREFRONT_URL_REVIEW_FILTER_COLUMNS.map(({ key, label, getOptions, type }) => ({
    key,
    label,
    type: type ?? 'select',
    options: type === 'dateRange' ? [] : (getOptions?.(sourceRows) ?? []),
  }));
}

function UrlCellRenderer(params: ICellRendererParams<StorefrontUrlSubmissionRow, string>) {
  const value = params.value?.trim();
  if (!value) {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    );
  }

  return (
    <GridCellWrap>
      <CopyableGridText
        text={value}
        textClassName="text-muted-foreground"
        copyMessage="URL copied to clipboard"
        copyLabel="Copy URL"
      />
    </GridCellWrap>
  );
}

function ReasonCellRenderer(params: ICellRendererParams<StorefrontUrlSubmissionRow>) {
  const row = params.data;
  if (!row) return null;
  return (
    <GridCellWrap>
      <span className="text-muted-foreground">{row.rejectionReason || '—'}</span>
    </GridCellWrap>
  );
}

function ReviewActionsCellRenderer(params: ICellRendererParams<StorefrontUrlSubmissionRow>) {
  const row = params.data;
  const context = params.context as StorefrontUrlReviewContext | undefined;
  if (!row || !context) return null;

  const isPending = row.status === 'PENDING';

  return (
    <div
      className="flex h-full flex-wrap items-center justify-end gap-1 py-1"
      onClick={(e) => e.stopPropagation()}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="outline"
              size="icon"
              className={cn(
                'tm-compact-icon-btn shrink-0 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10',
              )}
              aria-label="Approve"
              disabled={!isPending}
              onClick={() => context.onApprove(row)}
            >
              <BsCheckLg />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {isPending ? 'Approve' : 'Already reviewed'}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="outline"
              size="icon"
              className={cn(
                'tm-compact-icon-btn shrink-0 hover:border-destructive hover:text-destructive hover:bg-destructive/10',
              )}
              aria-label="Reject"
              disabled={!isPending}
              onClick={() => context.onReject(row)}
            >
              <BsXCircle />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {isPending ? 'Reject' : 'Already reviewed'}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export function createStorefrontUrlReviewColumnDefs(): ColDef<StorefrontUrlSubmissionRow>[] {
  return [
    {
      field: 'merchantName',
      colId: 'merchantName',
      headerName: columnLabel('merchantName'),
      flex: 1.1,
      minWidth: 180,
      cellRenderer: SubtitleCellRenderer,
    },
    {
      field: 'url',
      colId: 'url',
      headerName: columnLabel('url'),
      flex: 1.3,
      minWidth: 200,
      cellRenderer: UrlCellRenderer,
    },
    {
      field: 'status',
      colId: 'status',
      headerName: columnLabel('status'),
      width: 120,
      cellRenderer: StatusBadgeCellRenderer,
      pinned: 'right',
    },
    {
      field: 'rejectionReason',
      colId: 'rejectionReason',
      headerName: columnLabel('rejectionReason'),
      flex: 1,
      minWidth: 160,
      filterValueGetter: (params) => String(params.data?.rejectionReason ?? ''),
      cellRenderer: ReasonCellRenderer,
    },
    {
      colId: 'createdAt',
      headerName: columnLabel('createdAt'),
      width: 180,
      cellClass: 'text-muted-foreground',
      valueGetter: (params) => params.data?.createdAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
      filter: {
        component: 'agDateColumnFilter',
        doesFilterPass: () => true,
      },
      filterParams: {
        filterOptions: ['equals', 'inRange'],
        maxNumConditions: 1,
        buttons: ['clear', 'apply'],
        closeOnApply: true,
      },
    },
    {
      colId: 'updatedAt',
      headerName: 'Updated At',
      width: 180,
      cellClass: 'text-muted-foreground',
      sortable: true,
      filter: false,
      valueGetter: (params) => params.data?.updatedAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
    },
    {
      field: 'id',
      headerName: 'Action',
      pinned: 'right',
      width: 130,
      sortable: false,
      filter: false,
      cellRenderer: ReviewActionsCellRenderer,
    },
  ];
}
